import { v4 as uuid } from '@lukeed/uuid'
import { Stack, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { Fragment, ReactElement } from 'react'

import logoAppDynamics from 'assets/logos/appdynamics.svg'
import logoAWS from 'assets/logos/aws.svg'
import logoDatadog from 'assets/logos/datadog.svg'
import logoDynatrace from 'assets/logos/dynatrace.svg'
import logoGrafana from 'assets/logos/grafana.svg'
import logoNewRelic from 'assets/logos/newrelic.svg'
import logoPagerDuty from 'assets/logos/pagerduty.svg'
import logoPrometheus from 'assets/logos/prometheus.svg'
import Image from 'components/common/Image'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import Icon from 'components/Icon'
import theme from 'theme'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'
import { AMAZON_AWS_DOMAIN } from 'utils/status-checks/status-check-integrations-util-aws'
import { DYNATRACE_DOMAIN } from 'utils/status-checks/status-check-integrations-util-dynatrace'
import {
  grafanaApiAlertsRegex,
  grafanaBaseUrlRegex,
  grafanaRegex
} from 'utils/status-checks/status-check-integrations-util-grafana'
import { isValidUrl } from 'utils/strings'
import { ADDITIONAL_KNOWN_URLS, ENDPOINT_URLS } from 'utils/third-party'

export const createNewHeader = (
  key?: string,
  value?: string
): Array<string> => [key || '', value || '', uuid()] // Array of [key, value, id]

const LabelContent = ({ logo, text }: any) => {
  return (
    <span
      css={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}
    >
      {logo && (
        <Image
          css={{ height: theme.fontSizes[4], marginRight: theme.space[2] }}
          src={logo}
        />
      )}
      {text}
    </span>
  )
}

LabelContent.propTypes = {
  logo: PropTypes.string,
  text: PropTypes.string.isRequired
}

export function isCustomUrl(
  url?: string,
  observabilityToolType?: string
): boolean {
  const isKnownUrl = Object.values(ENDPOINT_URLS)
    .concat(...Object.values(ADDITIONAL_KNOWN_URLS))
    .some((endpointUrl) =>
      url?.toLowerCase().includes(endpointUrl?.toLowerCase())
    )

  // we have legacy status check code where this function is called and does not have immediate access to `observabilityToolType`
  // if observabilityToolType DNE, preserve the previous logic to see if it's a known url
  if (!observabilityToolType) {
    return !isKnownUrl
  }

  const isGrafanaUrl =
    grafanaRegex.test(url) ||
    grafanaBaseUrlRegex.test(url) ||
    grafanaApiAlertsRegex.test(url)

  const isDynatraceUrl =
    url?.includes(DYNATRACE_DOMAIN) &&
    observabilityToolType === ObservabilityToolType.DYNATRACE

  const isAWSUrl =
    url?.includes(AMAZON_AWS_DOMAIN) &&
    observabilityToolType === ObservabilityToolType.AWS

  return !isKnownUrl && !isDynatraceUrl && !isAWSUrl && !isGrafanaUrl
}

export function statusCheckMatchesKey(
  statusCheck?: Record<'endpointConfiguration', Record<'url', string>>,
  key?: string
): boolean {
  const statusCheckUrl = statusCheck?.endpointConfiguration?.url

  if (key === 'custom') return isCustomUrl(statusCheckUrl)

  const baseUrl = ENDPOINT_URLS[key]

  return statusCheckUrl.includes(baseUrl)
}

/**
 * Used to search against and return a logo associated with a 3rd party preset or saved Status Check
 * @param {String} logo
 */
export const getLogo = (logo?: string): string => {
  switch (logo) {
    case 'datadog':
      return logoDatadog
    case 'newrelic':
      return logoNewRelic
    case 'pagerduty':
      return logoPagerDuty
    case 'grafana':
      return logoGrafana
    case 'prometheus':
      return logoPrometheus
    case 'dynatrace':
      return logoDynatrace
    case 'appdynamics':
      return logoAppDynamics
    case 'aws':
      return logoAWS
    default:
      return null
  }
}

export const getPresetKeyFromURL = (url = ''): string => {
  const formattedURL = url.toLowerCase()
  const additionalKnownUrls = Object.values(ADDITIONAL_KNOWN_URLS).flat()

  if (
    formattedURL.startsWith(ENDPOINT_URLS.datadog) ||
    formattedURL.startsWith(ENDPOINT_URLS.datadog_us3) ||
    formattedURL.startsWith(ENDPOINT_URLS.datadog_us5) ||
    formattedURL.startsWith(ENDPOINT_URLS.datadog_eu) ||
    formattedURL.startsWith(ENDPOINT_URLS.datadog_us1_fed) ||
    additionalKnownUrls.find((url) => formattedURL.startsWith(url))
  ) {
    return 'datadog'
  } else if (formattedURL.startsWith(ENDPOINT_URLS.pagerduty)) {
    return 'pagerduty'
  } else if (formattedURL.startsWith(ENDPOINT_URLS.newrelic)) {
    return 'newrelic'
  } else if (formattedURL.includes(AMAZON_AWS_DOMAIN)) {
    return 'aws'
  } else if (formattedURL.includes(DYNATRACE_DOMAIN)) {
    return 'dynatrace'
  } else if (
    grafanaRegex.test(url) ||
    grafanaBaseUrlRegex.test(url) ||
    grafanaApiAlertsRegex.test(url)
  ) {
    return 'grafana'
  } else {
    return 'custom'
  }
}

export const getFormattedNameForPreset = (presetKey?: string): string => {
  switch (presetKey) {
    case 'datadog':
      return 'Datadog'
    case 'newrelic':
      return 'New Relic'
    case 'pagerduty':
      return 'Pagerduty'
    default:
      return 'Custom'
  }
}

export const getPresetKeyFromFormattedName = (name?: string): string => {
  switch (name) {
    case 'Datadog':
      return 'datadog'
    case 'New Relic':
      return 'newrelic'
    case 'Pagerduty':
      return 'pagerduty'
    default:
      return 'custom'
  }
}

export function getPresetKeyByValue<T>(
  object: Record<string, Record<'value', T>>,
  value: T
): string {
  return Object.keys(object).find((key) => object[key].value === value)
}

export const thirdPartyPresetsObject = {
  datadog: {
    label: {
      content: <LabelContent logo={getLogo('datadog')} text="Datadog" />,
      value: (
        <LabelContent logo={getLogo('datadog')} text={ENDPOINT_URLS.datadog} />
      )
    },
    value: ENDPOINT_URLS.datadog,
    documentation: {
      auth: (
        <Fragment>
          Need help finding Datadog endpoints? Read the{' '}
          <TrackedExternalLink href="https://docs.datadoghq.com/api/v1/monitors/#get-a-monitors-details">
            Datadog API Documentation
          </TrackedExternalLink>
        </Fragment>
      ),
      dataRetrieval: (
        <Fragment>
          Need help authenticating with Datadog? Read the{' '}
          <TrackedExternalLink href="https://docs.datadoghq.com/api/v1/authentication/">
            Datadog Documentation
          </TrackedExternalLink>
        </Fragment>
      )
    },
    headerInfo: [
      {
        headers: ['DD-API-KEY', ''],
        valueLabel: 'DataDog API Key'
      },
      {
        headers: ['DD-APPLICATION-KEY', ''],
        valueLabel: 'DataDog Application Key'
      }
    ]
  },
  newrelic: {
    label: {
      content: <LabelContent logo={getLogo('newrelic')} text="New Relic" />,
      value: (
        <LabelContent
          logo={getLogo('newrelic')}
          text={ENDPOINT_URLS.newrelic}
        />
      )
    },
    value: ENDPOINT_URLS.newrelic,

    documentation: {
      auth: (
        <Fragment>
          Need help finding New Relic endpoints? Read the{' '}
          <TrackedExternalLink href="https://docs.newrelic.com/docs/apis/get-started/intro-apis/types-new-relic-api-keys">
            New Relic API Documentation
          </TrackedExternalLink>
        </Fragment>
      ),
      dataRetrieval: (
        <Fragment>
          Need help authenticating with New Relic? Read the{' '}
          <TrackedExternalLink href="https://docs.newrelic.com/docs/alerts/rest-api-alerts/new-relic-alerts-rest-api/rest-api-calls-new-relic-alerts">
            New Relic Documentation
          </TrackedExternalLink>
        </Fragment>
      )
    },
    headerInfo: [
      {
        headers: ['X-API-Key', ''],
        valueLabel: 'New Relic API Key'
      }
    ]
  },
  pagerduty: {
    label: {
      content: <LabelContent logo={getLogo('pagerduty')} text="PagerDuty" />,
      value: (
        <LabelContent
          logo={getLogo('pagerduty')}
          text={ENDPOINT_URLS.pagerduty}
        />
      )
    },
    value: ENDPOINT_URLS.pagerduty,
    documentation: {
      auth: (
        <Fragment>
          Need help finding PagerDuty endpoints? Read the{' '}
          <TrackedExternalLink href="https://developer.pagerduty.com/docs/rest-api-v2/authentication/">
            PagerDuty API Documentation
          </TrackedExternalLink>
        </Fragment>
      ),
      dataRetrieval: (
        <Fragment>
          Need help authenticating with PagerDuty? Read the{' '}
          <TrackedExternalLink href="https://developer.pagerduty.com/api-reference/reference/REST/openapiv3.json/paths/~1incidents/get">
            PagerDuty Documentation
          </TrackedExternalLink>
        </Fragment>
      )
    },
    headerInfo: [
      {
        headers: ['Authorization', ''],
        valueLabel: 'PagerDuty API Key'
      }
    ]
  },
  custom: {
    label: {
      content: (
        <Stack alignItems="center" direction="row">
          <Icon
            css={{ fontSize: theme.fontSizes[3], marginRight: theme.space[2] }}
            icon="wrench"
          />
          <Typography>Custom</Typography>
        </Stack>
      ),
      value: (
        <Stack alignItems="center" direction="row">
          <Icon
            css={{ fontSize: theme.fontSizes[3], marginRight: theme.space[2] }}
            icon="wrench"
          />
          <Typography>Custom</Typography>
        </Stack>
      )
    },
    value: '',
    documentation: {
      auth: (
        <Fragment>
          Enter a publicly accessible endpoint. Reference{' '}
          <TrackedExternalLink href="http://www.gremlin.com/docs/scenarios/status-checks">
            Gremlin docs
          </TrackedExternalLink>{' '}
          for more information.
        </Fragment>
      ),
      dataRetrieval: (
        <Fragment>
          Enter authentication headers for your endpoint. Reference{' '}
          <TrackedExternalLink href="https://www.gremlin.com/docs/scenarios/status-checks">
            Gremlin docs
          </TrackedExternalLink>{' '}
          for more information.
        </Fragment>
      )
    },
    headerInfo: [{ headers: ['', ''], valueLabel: 'Value' }]
  }
}

/**
 * Takes existing/saved Status Check and returns a label with a 3rd party preset logo based on its value
 */
export function GenerateEndpointLabel({
  text,
  url
}: Record<string, string>): ReactElement<any, any> {
  // Find matching logo to return the assosciated image
  const logo = Object.entries(ENDPOINT_URLS).find(([, value]) =>
    url.includes(value)
  )?.[0]

  return <LabelContent logo={getLogo(logo)} text={text} />
}

GenerateEndpointLabel.propTypes = {
  text: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
}

// Returns the preset headers
// preset can be a url or a key
export function getPresetHeaders(preset: string): Array<any> {
  if (isEmpty(preset)) {
    return [createNewHeader()]
  }

  const presetKey = getPresetKey(preset)

  if (!isEmpty(presetKey)) {
    return Object.values(thirdPartyPresetsObject[presetKey].headerInfo).map(
      (info: { headers: string[] | string }) => createNewHeader(...info.headers)
    )
  }

  return [createNewHeader()]
}

// returns the url
// preset can be a url or a key
export function getPresetUrl(preset?: string): string {
  const presetKey = getPresetKey(preset)

  if (!isEmpty(presetKey)) {
    return thirdPartyPresetsObject[presetKey].value
  }

  return ''
}

export function getPresetKey(preset?: string): string {
  const isUrl = isValidUrl(preset)

  if (Object.keys(thirdPartyPresetsObject).includes(preset)) {
    return preset
  }

  return isUrl || isEmpty(preset)
    ? getPresetKeyByValue(thirdPartyPresetsObject, preset) || ''
    : ''
}
